import type { LayerSpecification, StyleSpecification } from "maplibre-gl";
import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import { normalizeColor } from "./colors";
import { getMainColor, MainColor, setMainColor } from "./main";
import {
  AnyLayer,
  isZoomBlend,
  layerById,
  readPath,
  ZOOM_SIDE_INDEX,
  ZoomSide,
  zoomSideOf,
} from "./paths";
import type { ColorPath } from "./types";

// A main colour can have one colour far out and another up close, blended
// smoothly in between. Each value in the family then becomes a two-stop zoom
// blend: ["interpolate", ["linear"], ["zoom"], ZOOMED_OUT, <far>, ZOOMED_IN,
// <close>]. A zoom blend has to be the outermost part of a value in
// MapLibre, so the whole value is wrapped, not each colour inside it.

// Roughly a whole country, and a street.
export const ZOOMED_OUT = 6;
export const ZOOMED_IN = 15;

export type { ZoomSide };

function containsZoom(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  if (value.length === 1 && value[0] === "zoom") return true;
  return value.some(containsZoom);
}

// A value can be wrapped when nothing in it depends on zoom yet. Legacy
// functions ({ stops: … }) cannot be put inside an expression.
function wrappable(value: unknown): boolean {
  return typeof value === "string" || (Array.isArray(value) && !containsZoom(value));
}

function valueAt(style: StyleSpecification, target: ColorPath): unknown {
  return layerById(style, target.layerId)?.paint?.[target.prop];
}

export function isSplit(style: StyleSpecification, main: MainColor): boolean {
  return (
    main.paths.length > 0 &&
    main.paths.every((target) => zoomSideOf(style, target) !== null)
  );
}

export function canSplit(style: StyleSpecification, main: MainColor): boolean {
  return (
    main.paths.length > 0 &&
    main.paths.every(
      (target) =>
        zoomSideOf(style, target) !== null || wrappable(valueAt(style, target))
    )
  );
}

function onSide(target: ColorPath, side: ZoomSide): ColorPath {
  return { ...target, path: [ZOOM_SIDE_INDEX[side], ...target.path.slice(1)] };
}

// The family as seen at one end of the zoom range.
function sideOf(style: StyleSpecification, main: MainColor, side: ZoomSide): MainColor {
  return {
    ...main,
    paths: main.paths.filter((target) => zoomSideOf(style, target) === side),
    anchor: onSide(main.anchor, side),
  };
}

export function getZoomColors(
  style: StyleSpecification,
  main: MainColor
): { out: string; in: string } | undefined {
  if (!isSplit(style, main)) return undefined;
  const far = getMainColor(style, sideOf(style, main, "out"));
  const close = getMainColor(style, sideOf(style, main, "in"));
  return far && close ? { out: far, in: close } : undefined;
}

// True when some colour of the family differs between far and close.
export function differsByZoom(style: StyleSpecification, main: MainColor): boolean {
  if (!isSplit(style, main)) return false;
  const layer = (target: ColorPath) => layerById(style, target.layerId);
  return main.paths.some((target) => {
    if (zoomSideOf(style, target) !== "out") return false;
    const far = readPath(layer(target), target);
    const close = readPath(layer(target), onSide(target, "in"));
    return (
      typeof far !== "string" ||
      typeof close !== "string" ||
      normalizeColor(far) !== normalizeColor(close)
    );
  });
}

export function zoomRange(
  style: StyleSpecification,
  main: MainColor
): { out: number; in: number } | undefined {
  const value = valueAt(style, main.anchor);
  return isZoomBlend(value)
    ? { out: value[3] as number, in: value[5] as number }
    : undefined;
}

type Edit = (paint: { [prop: string]: any }, prop: string, layerId: string) => void;

function editValues<S extends StyleSpecification>(
  style: S,
  targets: ColorPath[],
  edit: Edit
): S {
  const props = new Map<string, Set<string>>();
  targets.forEach((target) => {
    props.set(target.layerId, (props.get(target.layerId) || new Set()).add(target.prop));
  });
  const layers = style.layers.map((layer) => {
    const touched = props.get(layer.id);
    if (!touched) return layer;
    const before = (layer as AnyLayer).paint || {};
    const paint = cloneDeep(before);
    touched.forEach((prop) => edit(paint, prop, layer.id));
    return isEqual(paint, before) ? layer : ({ ...layer, paint } as LayerSpecification);
  });
  return { ...style, layers };
}

// Gives the family a far and a close colour, both equal to today's, so the
// map looks the same until one of them is changed. Values that are already
// a two-stop zoom blend count as split. Returns the style unchanged when a
// value cannot be split.
export function splitByZoom<S extends StyleSpecification>(style: S, main: MainColor): S {
  if (!canSplit(style, main) || isSplit(style, main)) return style;
  const unsplit = main.paths.filter((target) => zoomSideOf(style, target) === null);
  return editValues(style, unsplit, (paint, prop) => {
    const value = paint[prop];
    if (isZoomBlend(value)) return;
    paint[prop] = [
      "interpolate",
      ["linear"],
      ["zoom"],
      ZOOMED_OUT,
      cloneDeep(value),
      ZOOMED_IN,
      cloneDeep(value),
    ];
  });
}

// Sets the far or the close colour. Like a main colour, the swatch's shade
// lands exactly and the family's other shades on that side move with it.
export function setZoomColor<S extends StyleSpecification>(
  style: S,
  main: MainColor,
  side: ZoomSide,
  color: string
): S {
  if (!isSplit(style, main)) return style;
  return setMainColor(style, sideOf(style, main, side), color);
}

// Back to one colour per shade: the close colours stay, and a blend whose two
// ends have become the same is replaced by that end. A blend that another
// family still uses keeps its two ends.
export function joinZoom<S extends StyleSpecification>(style: S, main: MainColor): S {
  if (!isSplit(style, main)) return style;
  const far = main.paths.filter((target) => zoomSideOf(style, target) === "out");
  return editValues(style, main.paths, (paint, prop, layerId) => {
    const value = paint[prop];
    if (!isZoomBlend(value)) return;
    const local = { paint } as AnyLayer;
    far
      .filter((target) => target.layerId === layerId && target.prop === prop)
      .forEach((target) => {
        const close = readPath(local, onSide(target, "in"));
        if (typeof close !== "string") return;
        let node: any = value;
        target.path.slice(0, -1).forEach((step) => {
          node = node[step];
        });
        node[target.path[target.path.length - 1]] = close;
      });
    if (isEqual(value[4], value[6])) paint[prop] = value[6];
  });
}
